<template>
	<div
		ref="stageEl"
		class="clip-minimap relative w-full select-none rounded-lg bg-surface-1 ring-1 ring-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
		:class="cursorClass"
		:style="{ height: `${HEIGHT}px` }"
		tabindex="0"
		role="group"
		:aria-label="ariaLabel"
		@pointerdown="onPointerDown"
		@pointermove="onPointerMove"
		@pointerup="onPointerUp"
		@pointercancel="onPointerUp"
		@pointerleave="onPointerLeave"
		@keydown="onKeyDown"
	>
		<canvas ref="canvasEl" class="absolute inset-0 block h-full w-full rounded-lg" />

		<!-- Hover time -->
		<div
			v-if="hover && !drag"
			class="pointer-events-none absolute z-10 -translate-x-1/2 rounded bg-black/85 px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-white"
			:style="{ left: `${clamp(hover.x, 34, width - 34)}px`, top: '-26px' }"
		>
			{{ formatLocalTime(hover.t) }}
		</div>

		<div
			v-if="!extent"
			class="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-surface-8"
		>
			Overview appears once there is a recording
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { clamp, formatLocalTime, gapsBetween } from './dvrUtils';

// Overview of the whole DVR window. Recordings are muted, the viewport box is
// the brightest element and drives the detail timeline's `view`.
const props = defineProps({
	ranges: { type: Array, default: () => [] },
	dvrExtent: { type: Object, default: null },
	liveEdge: { type: Number, default: null },
	playhead: { type: Number, default: null },
	selection: { type: Object, default: null },
	selectionValid: { type: Boolean, default: true },
	// { from, duration } (v-model)
	view: { type: Object, required: true },
	minViewSec: { type: Number, default: 60 },
});

const emit = defineEmits(['update:view', 'user-interaction']);

const HEIGHT = 38;
const TRACK_TOP = 4;
const TRACK_BOTTOM = 24;
const TRACK_H = TRACK_BOTTOM - TRACK_TOP;
const LABEL_Y = 31;
// Half-width of the edge grip hit zone per pointer type.
const EDGE_HIT_PX = { mouse: 7, pen: 7, touch: 14 };

const stageEl = ref(null);
const canvasEl = ref(null);
const width = ref(0);
const drag = ref(null);
const hover = ref(null); // { x, t, kind }

let ctx = null;
let dpr = 1;
let rafId = 0;
let resizeObserver = null;
let hatch = null;

// Extent shown: the DVR window, stretched to the live edge / now so the
// viewport box can sit at the right edge while live.
const extent = computed(() => {
	const ext = props.dvrExtent;
	const first = props.ranges[0]?.start;
	const start = ext?.start ?? first;
	if (typeof start !== 'number') return null;
	const now = Date.now() / 1000;
	const last = props.ranges[props.ranges.length - 1]?.end ?? start;
	const end = Math.max(ext?.end ?? last, last, typeof props.liveEdge === 'number' ? props.liveEdge : 0, now);
	if (end - start < 1) return null;
	return { start, end };
});

const ariaLabel = computed(() => {
	const e = extent.value;
	if (!e) return 'Recording overview';
	return `Recording overview from ${formatLocalTime(e.start)} to ${formatLocalTime(e.end)}. Drag the box to move the timeline, drag its edges to zoom, click elsewhere to jump there. Left and right arrows pan.`;
});

const cursorClass = computed(() => {
	const k = drag.value?.kind || hover.value?.kind || 'none';
	if (k === 'edge-start' || k === 'edge-end') return 'cursor-ew-resize';
	if (k === 'box') return drag.value ? 'cursor-grabbing' : 'cursor-grab';
	return 'cursor-pointer';
});

function tToX(t) {
	const e = extent.value;
	if (!e) return 0;
	return ((t - e.start) / (e.end - e.start)) * width.value;
}

function xToT(x) {
	const e = extent.value;
	if (!e) return 0;
	return e.start + (x / width.value) * (e.end - e.start);
}

function boxPx() {
	return { x0: tToX(props.view.from), x1: tToX(props.view.from + props.view.duration) };
}

function hitTest(x, pointerType = 'mouse') {
	if (!extent.value) return 'none';
	const { x0, x1 } = boxPx();
	const hit = EDGE_HIT_PX[pointerType] || EDGE_HIT_PX.mouse;
	if (Math.abs(x - x0) <= hit) return 'edge-start';
	if (Math.abs(x - x1) <= hit) return 'edge-end';
	if (x > x0 && x < x1) return 'box';
	return 'none';
}

function setView(from, duration) {
	const e = extent.value;
	if (!e) return;
	const maxDur = Math.max(props.minViewSec, e.end - e.start);
	const dur = clamp(duration, props.minViewSec, maxDur);
	const f = clamp(from, e.start, e.end - dur);
	if (Math.abs(f - props.view.from) < 1e-6 && Math.abs(dur - props.view.duration) < 1e-6) return;
	emit('update:view', { from: f, duration: dur });
	emit('user-interaction');
}

function localX(e) {
	const rect = stageEl.value.getBoundingClientRect();
	return e.clientX - rect.left;
}

function onPointerDown(e) {
	if (e.button !== undefined && e.button !== 0) return;
	if (!extent.value) return;
	stageEl.value.focus({ preventScroll: true });
	stageEl.value.setPointerCapture?.(e.pointerId);
	const x = localX(e);
	const kind = hitTest(x, e.pointerType);
	const startT = xToT(x);
	if (kind === 'none') {
		// Centre the viewport on the click, then allow dragging it from there.
		const from = startT - props.view.duration / 2;
		setView(from, props.view.duration);
		drag.value = { kind: 'box', startX: x, startFrom: from, startDuration: props.view.duration };
		return;
	}
	drag.value = {
		kind,
		startX: x,
		startFrom: props.view.from,
		startDuration: props.view.duration,
	};
}

function onPointerMove(e) {
	const x = localX(e);
	const d = drag.value;
	if (!d) {
		hover.value = extent.value ? { x, t: xToT(x), kind: hitTest(x, e.pointerType) } : null;
		return;
	}
	const eSpan = extent.value ? extent.value.end - extent.value.start : 0;
	if (!eSpan || !width.value) return;
	const dt = ((x - d.startX) / width.value) * eSpan;
	if (d.kind === 'box') {
		setView(d.startFrom + dt, d.startDuration);
	} else if (d.kind === 'edge-start') {
		const end = d.startFrom + d.startDuration;
		const from = Math.min(d.startFrom + dt, end - props.minViewSec);
		setView(from, end - from);
	} else if (d.kind === 'edge-end') {
		setView(d.startFrom, Math.max(props.minViewSec, d.startDuration + dt));
	}
}

function onPointerUp(e) {
	drag.value = null;
	stageEl.value?.releasePointerCapture?.(e.pointerId);
}

function onPointerLeave() {
	if (!drag.value) hover.value = null;
}

function onKeyDown(e) {
	const v = props.view;
	let handled = true;
	switch (e.key) {
		case 'ArrowLeft':
			setView(v.from - v.duration * (e.shiftKey ? 0.5 : 0.1), v.duration);
			break;
		case 'ArrowRight':
			setView(v.from + v.duration * (e.shiftKey ? 0.5 : 0.1), v.duration);
			break;
		case '+':
		case '=':
			setView(v.from + v.duration * 0.15, v.duration * 0.7);
			break;
		case '-':
		case '_':
			setView(v.from - v.duration * 0.2, v.duration * 1.4);
			break;
		default:
			handled = false;
	}
	if (handled) e.preventDefault();
}

// ---- drawing --------------------------------------------------------------

const COLORS = {
	bg: 'rgb(20, 30, 61)',
	// Muted, desaturated slate-blue so the viewport box stays the brightest element.
	range: 'rgba(104, 130, 172, 0.55)',
	gap: 'rgb(13, 20, 43)',
	gapHatch: 'rgba(174, 186, 214, 0.22)',
	selection: 'rgb(32, 118, 255)',
	selectionBad: 'rgb(244, 100, 100)',
	live: 'rgb(226, 88, 88)',
	playhead: 'rgba(255, 255, 255, 0.95)',
	box: 'rgba(32, 118, 255, 0.16)',
	boxBorder: 'rgb(32, 118, 255)',
	boxGrip: 'rgb(214, 228, 255)',
	tick: 'rgba(174, 186, 214, 0.35)',
	label: 'rgb(174, 186, 214)',
	anchor: 'rgb(209, 219, 240)',
};

function makeHatch(c) {
	const size = 6;
	const off = document.createElement('canvas');
	off.width = size * dpr;
	off.height = size * dpr;
	const o = off.getContext('2d');
	o.scale(dpr, dpr);
	o.strokeStyle = COLORS.gapHatch;
	o.lineWidth = 1;
	o.beginPath();
	o.moveTo(-1, size + 1);
	o.lineTo(size + 1, -1);
	o.stroke();
	const p = c.createPattern(off, 'repeat');
	const m = typeof DOMMatrix !== 'undefined' ? new DOMMatrix() : null;
	if (p && m && p.setTransform) p.setTransform(m.scale(1 / dpr, 1 / dpr));
	return p;
}

function scheduleDraw() {
	if (rafId) return;
	rafId = requestAnimationFrame(() => {
		rafId = 0;
		draw();
	});
}

// Hourly ticks, thinned when there is no room for their labels.
function tickStep(spanSec, W) {
	const steps = [3600, 7200, 3 * 3600, 6 * 3600, 12 * 3600, 24 * 3600];
	for (const s of steps) {
		if ((s / spanSec) * W >= 56) return s;
	}
	return steps[steps.length - 1];
}

function draw() {
	const c = ctx;
	const W = width.value;
	if (!c || !W) return;
	const H = HEIGHT;
	c.save();
	c.setTransform(dpr, 0, 0, dpr, 0, 0);
	c.clearRect(0, 0, W, H);
	c.fillStyle = COLORS.bg;
	c.fillRect(0, 0, W, H);
	const e = extent.value;
	if (!e) {
		c.restore();
		return;
	}
	const span = e.end - e.start;
	const font = '10px Inter, ui-sans-serif, system-ui, sans-serif';

	// Gaps between recordings: dark base with the timeline's hatch
	for (const g of gapsBetween(props.ranges)) {
		const x0 = clamp(tToX(g.start), 0, W);
		const x1 = clamp(tToX(g.end), 0, W);
		if (x1 - x0 < 0.5) continue;
		c.fillStyle = COLORS.gap;
		c.fillRect(x0, TRACK_TOP, x1 - x0, TRACK_H);
		if (hatch) {
			c.fillStyle = hatch;
			c.fillRect(x0, TRACK_TOP, x1 - x0, TRACK_H);
		}
	}

	// Recorded ranges (muted)
	c.fillStyle = COLORS.range;
	for (const r of props.ranges) {
		const x0 = clamp(tToX(r.start), 0, W);
		const x1 = clamp(tToX(r.end), 0, W);
		c.fillRect(x0, TRACK_TOP + 3, Math.max(1.5, x1 - x0), TRACK_H - 6);
	}

	// Selection: bright accent slice
	if (props.selection) {
		const x0 = clamp(tToX(props.selection.start), 0, W);
		const x1 = clamp(tToX(props.selection.end), 0, W);
		c.fillStyle = props.selectionValid ? COLORS.selection : COLORS.selectionBad;
		c.fillRect(x0, TRACK_TOP, Math.max(2, x1 - x0), TRACK_H);
	}

	// Live edge
	if (typeof props.liveEdge === 'number') {
		const x = Math.round(clamp(tToX(props.liveEdge), 1, W - 1));
		c.strokeStyle = COLORS.live;
		c.lineWidth = 2;
		c.beginPath();
		c.moveTo(x, TRACK_TOP - 2);
		c.lineTo(x, TRACK_BOTTOM + 2);
		c.stroke();
	}

	// Playhead
	if (typeof props.playhead === 'number') {
		const x = Math.round(tToX(props.playhead)) + 0.5;
		if (x >= 0 && x <= W) {
			c.strokeStyle = COLORS.playhead;
			c.lineWidth = 1;
			c.beginPath();
			c.moveTo(x, TRACK_TOP - 2);
			c.lineTo(x, TRACK_BOTTOM + 2);
			c.stroke();
		}
	}

	// Viewport box stays on the track so it cannot cover hour labels.
	const { x0, x1 } = boxPx();
	const bx0 = clamp(x0, 0, W);
	const bw = Math.max(8, clamp(x1, 0, W) - bx0);
	c.fillStyle = COLORS.box;
	c.fillRect(bx0, TRACK_TOP, bw, TRACK_H);
	c.strokeStyle = COLORS.boxBorder;
	c.lineWidth = 2;
	c.strokeRect(bx0 + 1, TRACK_TOP + 1, bw - 2, TRACK_H - 2);
	c.fillStyle = COLORS.boxGrip;
	for (const gx of [bx0 + 1, bx0 + bw - 1]) {
		c.fillRect(Math.round(gx) - 1.5, (TRACK_TOP + TRACK_BOTTOM) / 2 - 5, 3, 10);
	}

	// Hourly ticks + labels last, so they stay readable over the viewport box.
	const step = tickStep(span, W);
	const tzOffsetSec = new Date(e.start * 1000).getTimezoneOffset() * -60;
	const first = Math.ceil((e.start + tzOffsetSec) / step) * step - tzOffsetSec;
	c.font = font;
	c.textBaseline = 'middle';
	const startLabel = formatLocalTime(e.start, false);
	const liveT = typeof props.liveEdge === 'number' ? props.liveEdge : e.end;
	const endLabel = typeof props.liveEdge === 'number' ? `Live ${formatLocalTime(liveT)}` : `Now ${formatLocalTime(e.end)}`;
	const startPad = c.measureText(startLabel).width + 10;
	const endPad = c.measureText(endLabel).width + 10;
	c.textAlign = 'center';
	for (let t = first; t < e.end; t += step) {
		const x = Math.round(tToX(t)) + 0.5;
		c.strokeStyle = COLORS.tick;
		c.lineWidth = 1;
		c.beginPath();
		c.moveTo(x, TRACK_BOTTOM);
		c.lineTo(x, TRACK_BOTTOM + 3);
		c.stroke();
		if (x > startPad && x < W - endPad) {
			c.fillStyle = COLORS.label;
			c.fillText(formatLocalTime(t, false), x, LABEL_Y);
		}
	}

	c.fillStyle = COLORS.anchor;
	c.textAlign = 'left';
	c.fillText(startLabel, 4, LABEL_Y);
	c.textAlign = 'right';
	c.fillText(endLabel, W - 4, LABEL_Y);

	c.restore();
}

function resize() {
	const stage = stageEl.value;
	const canvas = canvasEl.value;
	if (!stage || !canvas) return;
	width.value = Math.max(0, Math.floor(stage.getBoundingClientRect().width));
	dpr = Math.min(3, window.devicePixelRatio || 1);
	canvas.width = Math.floor(width.value * dpr);
	canvas.height = Math.floor(HEIGHT * dpr);
	canvas.style.width = `${width.value}px`;
	canvas.style.height = `${HEIGHT}px`;
	ctx = canvas.getContext('2d');
	hatch = ctx ? makeHatch(ctx) : null;
	scheduleDraw();
}

watch(
	() => [props.ranges, props.dvrExtent, props.liveEdge, props.playhead, props.selection, props.selectionValid, props.view],
	() => scheduleDraw(),
	{ deep: true },
);

onMounted(() => {
	resize();
	resizeObserver = new ResizeObserver(() => resize());
	resizeObserver.observe(stageEl.value);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	cancelAnimationFrame(rafId);
});
</script>

<style scoped>
.clip-minimap {
	touch-action: pan-y;
}
</style>
